import { localGet, localSet, remoteGet, remoteSet } from '@/lib/appStorage';

// ── Catalogue des mini-applications du compte utilisateur ──────────────
// Ces apps sont disponibles pour les comptes « normaux » (sans boutique).
// Le SuperAdmin peut masquer / publier chaque app via des réglages
// stockés dans `core_settings` (niveau plateforme).

export type ClientAppCategory = 'Études' | 'Organisation' | 'Documents' | 'IA';

/** À qui l'application peut être proposée. */
export type AppAudience = 'user' | 'merchant';

export interface ClientApp {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  icon: string;
  category: ClientAppCategory;
  priority: 'P1' | 'P2';
  features: string[];
  offline: boolean;
  image: string;
  route: string;
  requiresInstall: boolean;
  /** Audiences autorisées par défaut (le SuperAdmin peut ajuster). */
  audience?: AppAudience[];
}

export const CLIENT_APPS: ClientApp[] = [
  {
    slug: 'zifek-notes',
    name: 'Zifek Notes',
    tagline: 'Carnets, matières et notes structurées',
    description:
      "Un cahier numérique organisé par année, semestre, matière et chapitre. Écrivez, recherchez, marquez vos notes importantes et générez un résumé de révision — même sans connexion.",
    icon: 'ri-sticky-note-line',
    category: 'Études',
    priority: 'P1',
    features: [
      'Carnets par année, semestre, matière et chapitre',
      'Notes avec titres, listes, liens et images',
      'Enregistrement automatique et historique de versions',
      'Recherche locale instantanée (hors ligne)',
      'Favoris, notes importantes et à réviser',
      'Résumé et préparation de révision avec l’IA',
    ],
    offline: true,
    image:
      'https://readdy.ai/api/search-image?query=Clean%20minimalist%20digital%20notebook%20interface%20mockup%20with%20cream%20paper%20texture%2C%20soft%20warm%20beige%20background%2C%20elegant%20typography%2C%20calm%20study%20atmosphere%2C%20editorial%20flat%20lay%20composition%2C%20no%20text%20characters%2C%20high%20detail&width=800&height=500&seq=client-app-notes-01&orientation=landscape',
    route: '/mon-compte/apps/zifek-notes',
    requiresInstall: true,
    audience: ['user'],
  },
  {
    slug: 'zifek-documents',
    name: 'Zifek Documents',
    tagline: 'Le moteur documentaire, côté personnel',
    description:
      "Créez, importez et exportez vos documents personnels avec le même éditeur que l’espace professionnel. Dossiers, versions, partage contrôlé et reprise de brouillon.",
    icon: 'ri-file-text-line',
    category: 'Documents',
    priority: 'P1',
    features: [
      'Éditeur documentaire existant réutilisé',
      'Dossiers et organisation personnelle',
      'Import Word, Excel, PowerPoint, CSV et PDF',
      'Export PDF / DOCX selon le format du document',
      'Brouillons et versions locales',
      'Partage explicite uniquement',
    ],
    offline: false,
    image:
      'https://readdy.ai/api/search-image?query=Minimalist%20personal%20document%20workspace%20with%20stacked%20white%20papers%20and%20a%20soft%20sage%20green%20background%2C%20clean%20studio%20lighting%2C%20editorial%20still%20life%2C%20no%20text%20characters%2C%20refined%20neutral%20palette%2C%20high%20detail&width=800&height=500&seq=client-app-docs-01&orientation=landscape',
    route: '/mon-compte/apps/zifek-documents',
    requiresInstall: false,
    audience: ['user', 'merchant'],
  },
  {
    slug: 'zifek-tasks',
    name: 'Zifek Tasks & Planner',
    tagline: 'Tâches, devoirs, examens et rappels',
    description:
      "Un seul endroit pour vos tâches, vos cours et vos échéances. Vues jour, semaine et mois, priorités, durée estimée et rappels sur votre appareil.",
    icon: 'ri-task-line',
    category: 'Organisation',
    priority: 'P1',
    features: [
      'Tâches avec matière, priorité et échéance',
      'Événements : cours, examens, révisions',
      'Vues jour / semaine / mois',
      'Rappels locaux sur l’appareil',
      'Tâches en retard et échéances proches',
      'Association à une note ou un jeu de cartes',
    ],
    offline: true,
    image:
      'https://readdy.ai/api/search-image?query=Calm%20planning%20desk%20scene%20with%20blank%20weekly%20planner%2C%20wooden%20pencil%20and%20ceramic%20cup%2C%20warm%20terracotta%20and%20sand%20tones%2C%20soft%20daylight%2C%20minimalist%20editorial%20composition%2C%20no%20text%20characters%2C%20high%20detail&width=800&height=500&seq=client-app-tasks-01&orientation=landscape',
    route: '/mon-compte/apps/zifek-tasks',
    requiresInstall: true,
    audience: ['user'],
  },
  {
    slug: 'zifek-flashcards',
    name: 'Zifek Flashcards & Quiz',
    tagline: 'Transformez vos notes en entraînement',
    description:
      "Créez des jeux de cartes, révisez en mode cartes, quiz ou examen chronométré. Le score est calculé par l’application à partir de votre corrigé, jamais par l’IA.",
    icon: 'ri-stack-line',
    category: 'Études',
    priority: 'P2',
    features: [
      'Jeux de cartes par matière et chapitre',
      'Questions à choix multiples, vrai/faux, réponses courtes',
      'Modes cartes, quiz et examen chronométré',
      'Répétition espacée des cartes difficiles',
      'Génération de brouillons depuis les notes',
      'Téléchargement pour réviser hors ligne',
    ],
    offline: true,
    image:
      'https://readdy.ai/api/search-image?query=Neat%20stack%20of%20blank%20study%20cards%20on%20a%20linen%20surface%20with%20soft%20shadows%2C%20muted%20olive%20and%20cream%20palette%2C%20minimal%20editorial%20product%20photography%2C%20calm%20study%20mood%2C%20no%20text%20characters%2C%20high%20detail&width=800&height=500&seq=client-app-flash-01&orientation=landscape',
    route: '/mon-compte/apps/zifek-flashcards',
    requiresInstall: true,
    audience: ['user'],
  },
  {
    slug: 'zifek-study-ai',
    name: 'Zifek Study AI',
    tagline: 'L’assistant qui lit ce que vous choisissez',
    description:
      "Posez vos questions à partir de vos notes et documents sélectionnés. Résumés, explications, plans de révision et questions d’entraînement, avec les références utilisées.",
    icon: 'ri-sparkling-line',
    category: 'IA',
    priority: 'P2',
    features: [
      'Sélection précise des sources autorisées',
      'Résumé, explication simplifiée, plan de révision',
      'Questions d’entraînement et fiches',
      'Références aux extraits utilisés',
      'Historique des conversations',
      'Révocation de l’accès aux sources',
    ],
    offline: false,
    image:
      'https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20cloud%20with%20soft%20geometric%20shapes%20in%20amber%20and%20cream%2C%20subtle%20glowing%20particles%2C%20minimal%20futuristic%20study%20concept%20background%2C%20no%20text%20characters%2C%20high%20detail%2C%20elegant&width=800&height=500&seq=client-app-studyai-01&orientation=landscape',
    route: '/mon-compte/apps/zifek-study-ai',
    requiresInstall: false,
    audience: ['user', 'merchant'],
  },
];

export function getClientApp(slug: string): ClientApp | undefined {
  return CLIENT_APPS.find((app) => app.slug === slug);
}

// ── État d'installation par utilisateur ────────────────────────────────

const INSTALL_KEY = 'client_app_installs';

export async function readInstalledApps(userId: number): Promise<string[]> {
  const local = await localGet<string[]>(`user::${userId}:${INSTALL_KEY}`);
  if (local) return local;
  const remote = await remoteGet<string[]>('user', '', INSTALL_KEY, userId);
  if (remote && Array.isArray(remote)) {
    await localSet(`user::${userId}:${INSTALL_KEY}`, remote);
    return remote;
  }
  return [];
}

export async function writeInstalledApps(userId: number, slugs: string[]): Promise<void> {
  await localSet(`user::${userId}:${INSTALL_KEY}`, slugs);
  await remoteSet('user', '', INSTALL_KEY, slugs, userId);
}

// ── Disponibilité gérée par le SuperAdmin ──────────────────────────────

export interface ClientAppOverride {
  available: boolean;
  priority?: 'P1' | 'P2';
  /** Visible dans l'App Store des utilisateurs normaux. */
  showToUsers?: boolean;
  /** Visible dans l'App Store des marchands. */
  showToMerchants?: boolean;
}

export type ClientAppOverrides = Record<string, ClientAppOverride>;

const OVERRIDE_KEY = 'client_apps_catalog';

export async function readClientAppOverrides(): Promise<ClientAppOverrides> {
  const cached = await localGet<ClientAppOverrides>(`platform:::${OVERRIDE_KEY}`);
  if (cached) return cached;
  const remote = await remoteGet<ClientAppOverrides>('platform', '', OVERRIDE_KEY);
  if (remote && typeof remote === 'object') {
    await localSet(`platform:::${OVERRIDE_KEY}`, remote);
    return remote;
  }
  return {};
}

export async function writeClientAppOverride(slug: string, patch: ClientAppOverride): Promise<ClientAppOverrides> {
  const current = await readClientAppOverrides();
  const next: ClientAppOverrides = { ...current, [slug]: { ...current[slug], ...patch } };
  await localSet(`platform:::${OVERRIDE_KEY}`, next);
  await remoteSet('platform', '', OVERRIDE_KEY, next);
  return next;
}

export function isAppAvailable(app: ClientApp, overrides: ClientAppOverrides): boolean {
  const override = overrides[app.slug];
  if (!override) return true;
  return override.available !== false;
}

/** App visible par un utilisateur normal ? */
export function isAppVisibleToUsers(app: ClientApp, overrides: ClientAppOverrides): boolean {
  if (!isAppAvailable(app, overrides)) return false;
  const override = overrides[app.slug];
  const fallback = (app.audience || ['user']).includes('user');
  return override?.showToUsers === undefined ? fallback : override.showToUsers;
}

/** App visible dans l'espace marchand ? */
export function isAppVisibleToMerchants(app: ClientApp, overrides: ClientAppOverrides): boolean {
  if (!isAppAvailable(app, overrides)) return false;
  const override = overrides[app.slug];
  const fallback = (app.audience || ['user']).includes('merchant');
  return override?.showToMerchants === undefined ? fallback : override.showToMerchants;
}

/** Met à jour un réglage d'audience en conservant le reste. */
export async function writeClientAppAudience(
  slug: string,
  patch: Pick<ClientAppOverride, 'showToUsers' | 'showToMerchants'>,
): Promise<ClientAppOverrides> {
  const current = await readClientAppOverrides();
  const next: ClientAppOverrides = { ...current, [slug]: { available: true, ...current[slug], ...patch } };
  await localSet(`platform:::${OVERRIDE_KEY}`, next);
  await remoteSet('platform', '', OVERRIDE_KEY, next);
  return next;
}

export function appPriority(app: ClientApp, overrides: ClientAppOverrides): 'P1' | 'P2' {
  return overrides[app.slug]?.priority || app.priority;
}